import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import 'react-calendar/dist/Calendar.css';
import './css/calendar.css';
import attractionsData from '../data/attractions.json';
import allAttractions from '../data/edinburgh_attractions.json';
import { getImage } from '../utils/imageImports';

const CalendarPage = () => {
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedAttraction, setSelectedAttraction] = useState(null);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [eventTime, setEventTime] = useState('12:00');
  const [eventNotes, setEventNotes] = useState('');
  const [eventName, setEventName] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [suggestedAttractions, setSuggestedAttractions] = useState([]);
  const navigate = useNavigate();

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    enter: { 
      opacity: 1,
      transition: { duration: 0.7, when: "beforeChildren" }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.5 } 
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "spring",
        damping: 12,
        stiffness: 100
      }
    }
  };

  // Load stored events from localStorage and check if there's a selected attraction
  useEffect(() => {
    const storedEvents = localStorage.getItem('calendarEvents');
    if (storedEvents) {
      setEvents(JSON.parse(storedEvents));
    }

    const attraction = sessionStorage.getItem('selectedAttraction');
    if (attraction) {
      const parsedAttraction = JSON.parse(attraction);
      // Use getImage to properly process the image path
      if (parsedAttraction.image && typeof parsedAttraction.image === 'string') {
        parsedAttraction.image = getImage(parsedAttraction.image);
      }
      setSelectedAttraction(parsedAttraction);
      setEventName(parsedAttraction.title);
      setShowAddEventModal(true);
      // Clear from session storage to prevent it from reappearing on refresh
      sessionStorage.removeItem('selectedAttraction');
    }
    
    // Add a small delay for animation
    setTimeout(() => {
      setIsLoaded(true);
    }, 300);
  }, []);

  // Generate personalized suggestions based on user's events
  useEffect(() => {
    if (events.length > 0) {
      // Extract categories and interests from existing events
      const userEventNames = events.map(event => event.title.toLowerCase());
      const userInterests = new Set();
      
      // Look for keywords in event names
      userEventNames.forEach(name => {
        if (name.includes('castle') || name.includes('palace')) userInterests.add('historical');
        if (name.includes('garden') || name.includes('hill') || name.includes('park') || name.includes('seat')) userInterests.add('outdoor');
        if (name.includes('ghost') || name.includes('vault') || name.includes('tour') || name.includes('close')) userInterests.add('spooky');
        if (name.includes('museum') || name.includes('gallery')) userInterests.add('cultural');
        if (name.includes('whisky') || name.includes('experience') || name.includes('food')) userInterests.add('culinary');
      });

      // Get existing event IDs for exclusion
      const existingEventIds = new Set(
        events
          .filter(event => event.attractionId) // Only events with attraction IDs
          .map(event => event.attractionId)
      );

      // Filter attractions that match user interests
      let suggestions = allAttractions.filter(attraction => {
        // Don't suggest attractions already in calendar
        if (existingEventIds.has(attraction.id)) return false;
        
        const title = attraction.title.toLowerCase();
        const description = (attraction.description || '').toLowerCase();
        
        // Match based on interests
        for (let interest of userInterests) {
          if (interest === 'historical' && (title.includes('castle') || title.includes('palace') || 
              description.includes('history') || description.includes('historic'))) {
            return true;
          }
          if (interest === 'outdoor' && (title.includes('garden') || title.includes('park') || 
              title.includes('hill') || description.includes('outdoor') || 
              description.includes('walk') || description.includes('nature'))) {
            return true;
          }
          if (interest === 'spooky' && (title.includes('ghost') || title.includes('vault') || 
              description.includes('haunted') || description.includes('mystery'))) {
            return true;
          }
          if (interest === 'cultural' && (title.includes('museum') || title.includes('gallery') || 
              description.includes('art') || description.includes('culture'))) {
            return true;
          }
          if (interest === 'culinary' && (title.includes('food') || title.includes('whisky') || 
              description.includes('taste') || description.includes('cuisine'))) {
            return true;
          }
        }
        
        return false;
      });
      
      // If user doesn't have enough events for interests, provide popular attractions
      if (suggestions.length < 3) {
        const popularIds = [1, 2, 5, 8]; // IDs of popular attractions
        const additionalSuggestions = allAttractions
          .filter(attraction => 
            popularIds.includes(attraction.id) && 
            !existingEventIds.has(attraction.id) && 
            !suggestions.some(s => s.id === attraction.id)
          );
        
        suggestions = [...suggestions, ...additionalSuggestions];
      }
      
      // Limit to 4 suggestions
      setSuggestedAttractions(suggestions.slice(0, 4));
    } else {
      // If no events, suggest popular attractions
      setSuggestedAttractions(allAttractions.slice(0, 4));
    }
  }, [events]);

  // Save events to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('calendarEvents', JSON.stringify(events));
  }, [events]);

  const handleDateClick = (value) => {
    setDate(value);
  };

  const addEvent = () => {
    const newEvent = {
      id: Date.now(), // unique ID for the event
      title: eventName,
      date: date.toDateString(),
      time: eventTime,
      notes: eventNotes,
      image: selectedAttraction?.image ? getImage(selectedAttraction.image) : null,
      attractionId: selectedAttraction?.id || null
    };

    setEvents([...events, newEvent]);
    setShowAddEventModal(false);
    setEventName('');
    setEventTime('12:00');
    setEventNotes('');
    setSelectedAttraction(null);
  };

  const removeEvent = (id) => {
    setEvents(events.filter(event => event.id !== id));
  };

  // Function to handle adding a suggested attraction to calendar
  const handleAddSuggestion = (attraction) => {
    setSelectedAttraction(attraction);
    setEventName(attraction.title);
    setShowAddEventModal(true);
  };

  // Function to redirect to an attraction's details
  const navigateToAttraction = (attractionId) => {
    navigate(`/todo?attraction=${attractionId}`);
  };

  // Function to get events for the selected date
  const getEventsForSelectedDate = () => {
    return events.filter(event => event.date === date.toDateString());
  };

  // Filter out only the unique dates that have events
  const datesWithEvents = [...new Set(events.map(event => event.date))];

  // Custom tile content for the calendar
  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const dateStr = date.toDateString();
      const hasEvent = datesWithEvents.includes(dateStr);
      
      return hasEvent ? (
        <div className="calendar-event-marker"></div>
      ) : null;
    }
  };

  return (
    <motion.div 
      className="calendar-page"
      initial="initial"
      animate="enter"
      exit="exit"
      variants={pageVariants}
    >
      <motion.div 
        className="calendar-header"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h1>Edinburgh Events Calendar</h1>
        <p className="gothic-subtitle">Plan your adventures in the ancient city</p>
      </motion.div>

      <div className="calendar-container">
        <motion.div 
          className="calendar-section"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <Calendar 
            onChange={handleDateClick} 
            value={date} 
            tileContent={tileContent}
            className="gothic-calendar"
          />
          <motion.button 
            className="add-event-btn"
            onClick={() => setShowAddEventModal(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="add-icon">+</span>
            <span>Add New Event</span>
          </motion.button>
        </motion.div>

        <motion.div 
          className="event-list-section"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <motion.h2 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Events for {date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </motion.h2>
          
          <motion.div 
            className="events-list"
            variants={containerVariants}
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
          >
            <AnimatePresence>
              {getEventsForSelectedDate().length > 0 ? (
                getEventsForSelectedDate().map(event => (
                  <motion.div 
                    className="event-card"
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    layout
                    variants={itemVariants}
                    onClick={() => event.attractionId && navigateToAttraction(event.attractionId)}
                    style={{ cursor: event.attractionId ? 'pointer' : 'default' }}
                  >
                    {event.image && (
                      <div className="event-image">
                        <img src={event.image} alt={event.title} />
                        <div className="event-gradient-overlay"></div>
                      </div>
                    )}
                    <div className="event-details">
                      <h3>{event.title}</h3>
                      <p className="event-time">
                        <span className="icon">🕒</span> {event.time}
                      </p>
                      {event.notes && (
                        <p className="event-notes">{event.notes}</p>
                      )}
                      <motion.button 
                        className="remove-event-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeEvent(event.id);
                        }}
                        whileHover={{ scale: 1.1, backgroundColor: '#ff3860' }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Remove
                      </motion.button>
                    </div>
                  </motion.div>
                ))
              ) : (
                <motion.div 
                  className="no-events-message"
                  variants={itemVariants}
                >
                  <p>No events scheduled for this day.</p>
                  <p className="suggestion">Click "Add New Event" to plan your adventure.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </div>

      {/* Suggested Activities Section */}
      <motion.div 
        className="suggested-activities-section"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <motion.h2 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          Suggested Activities
        </motion.h2>
        <p className="suggestion-subtitle">Based on your planned events, you might also enjoy:</p>
        
        <div className="suggestions-grid">
          {suggestedAttractions.map((attraction) => (
            <motion.div 
              className="suggestion-card" 
              key={attraction.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                delay: 0.8 + Math.random() * 0.3,
                duration: 0.6 
              }}
              whileHover={{ 
                y: -10,
                boxShadow: "0 20px 25px rgba(0, 0, 0, 0.15), 0 10px 10px rgba(0, 0, 0, 0.12)"
              }}
              onClick={() => navigateToAttraction(attraction.id)}
            >
              <div className="suggestion-image">
                <img src={getImage(attraction.image)} alt={attraction.title} />
                <div className="suggestion-gradient-overlay"></div>
              </div>
              <div className="suggestion-content">
                <h3>{attraction.title}</h3>
                <p className="suggestion-description">
                  {attraction.description && attraction.description.length > 80 
                    ? `${attraction.description.substring(0, 80)}...` 
                    : attraction.description}
                </p>
                <div className="suggestion-actions">
                  <motion.button
                    className="view-details-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToAttraction(attraction.id);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    View Details
                  </motion.button>
                  <motion.button
                    className="add-to-calendar-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddSuggestion(attraction);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    + Add to Calendar
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Add Event Modal */}
      <AnimatePresence>
        {showAddEventModal && (
          <motion.div 
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowAddEventModal(false)}
          >
            <motion.div 
              className="event-modal"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={e => e.stopPropagation()}
            >
              <h2>{selectedAttraction ? 'Schedule Your Visit' : 'Add New Event'}</h2>
              
              {selectedAttraction && (
                <div className="selected-attraction">
                  <img src={getImage(selectedAttraction.image)} alt={selectedAttraction.title} />
                  <h3>{selectedAttraction.title}</h3>
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="event-name">Event Name</label>
                <input 
                  type="text" 
                  id="event-name" 
                  value={eventName} 
                  onChange={e => setEventName(e.target.value)}
                  placeholder="Event name"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="event-date">Date</label>
                <div className="date-display">{date.toLocaleDateString()}</div>
              </div>
              
              <div className="form-group">
                <label htmlFor="event-time">Time</label>
                <input 
                  type="time" 
                  id="event-time" 
                  value={eventTime} 
                  onChange={e => setEventTime(e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="event-notes">Notes (optional)</label>
                <textarea 
                  id="event-notes" 
                  value={eventNotes} 
                  onChange={e => setEventNotes(e.target.value)}
                  placeholder="Add any additional notes here..."
                />
              </div>
              
              <div className="modal-actions">
                <motion.button 
                  className="cancel-btn"
                  onClick={() => setShowAddEventModal(false)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancel
                </motion.button>
                <motion.button 
                  className="save-btn"
                  onClick={addEvent}
                  disabled={!eventName.trim()}
                  whileHover={{ scale: 1.05, backgroundColor: '#3a7d44' }}
                  whileTap={{ scale: 0.95 }}
                >
                  Save Event
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CalendarPage;
